import { QUIZ_LIMITS_REACHED_ERROR } from '@showbie-socrative/socrative-utils';
import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { PaywallScreens } from '~analytics/types';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions/';
import { increaseQuizCount } from '~teacher/hook/useQuizLimit';
import {
  quizzesQueryKey,
  restoreQuizzesFoldersInCache,
} from '~teacher/hook/useQuizzes';
import { proCheck } from '~teacher/util/proCheck';

import { isAxiosError } from '../../api/isAxiosError';
import {
  RestoreQuizFolderParam,
  RestoreQuizFolderResponse,
  restoreQuizFolder,
} from '../../api/quiz/restoreQuizFolder/restoreQuizFolder';

export function useRestoreQuizFolder(
  items: (string | number)[],
  paywallDispatch,
  quizLimitPaywallDispatch
) {
  const queryClient = useQueryClient();
  let quizFolderRestored = false;

  return useMutation(restoreQuizFolder, {
    onMutate: async (restoreQuizFolderParams: RestoreQuizFolderParam) => {
      const oldCache = queryClient.getQueryData(quizzesQueryKey.quiz);
      if (proCheck.canRestoreTrash()) {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(quizzesQueryKey.quiz);
        restoreQuizzesFoldersInCache(
          queryClient,
          restoreQuizFolderParams.quizIds,
          restoreQuizFolderParams.folderIds
        );
        quizFolderRestored = true;
        return { oldCache };
      } else {
        paywallDispatch.paywallDispatch({
          type: 'OPEN',
          touch: [
            PaywallScreens.QuizListRestoreQuizFolder,
            PaywallScreens.QuizListMoveItem,
          ],
        });
        return { oldCache };
      }
    },

    onError: (error, newCache, ctx) => {
      if (isAxiosError(error)) {
        const data = error.response.data;

        if (data.code === QUIZ_LIMITS_REACHED_ERROR.code) {
          quizLimitPaywallDispatch({
            type: 'OPEN',
            touch: [
              PaywallScreens.QuizListLimitReached,
              PaywallScreens.QuizListMoveItem,
            ],
          });
        }
      } else {
        apiToaster([...items], QuizToastActions.RESTORE, error);
        queryClient.setQueryData(quizzesQueryKey.quiz, ctx.oldCache);
      }
    },

    onSuccess: () => {
      if (quizFolderRestored) {
        increaseQuizCount(queryClient);
        apiToaster([...items], QuizToastActions.RESTORE);
      }
    },

    onSettled: () => {
      return queryClient.invalidateQueries(quizzesQueryKey.quiz);
    },
  });
}

export type RestoreQuizError = unknown;

export type RestoreQuizFolderMutation = UseMutateFunction<
  RestoreQuizFolderResponse,
  RestoreQuizError,
  RestoreQuizFolderParam
>;
